import React, { useEffect, useState } from 'react';
import { istDateTime } from '../lib/ist';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle,
  RefreshCw, Database, IndianRupee, CalendarRange, Undo2,
} from 'lucide-react';
import { API, useAuth } from '../App';

export default function ImportHistory() {
  const { token } = useAuth();
  const [kiosks, setKiosks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // {upload_id, filename, sheets, cross_sheet_duplicates}
  const [mappings, setMappings] = useState({}); // sheet -> kiosk_id ('' = skip)
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [undoing, setUndoing] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBatches = async () => {
    try {
      const r = await axios.get(`${API}/admin/import/batches`, { headers });
      setBatches(r.data.batches || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    axios.get(`${API}/kiosks`, { headers }).then((r) => setKiosks(r.data || [])).catch(() => {});
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setPreview(null);
    setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/import/preview`, fd, { headers });
      setPreview(r.data);
      setMappings({});
      toast.success(`Parsed ${r.data.sheets.length} transaction sheet(s) — review below, nothing imported yet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    }
    setUploading(false);
  };

  const commit = async () => {
    const maps = Object.entries(mappings)
      .filter(([, kid]) => kid)
      .map(([sheet, kiosk_id]) => ({ sheet, kiosk_id }));
    if (!maps.length) { toast.error('Select a booth for at least one sheet'); return; }
    setCommitting(true);
    try {
      const r = await axios.post(`${API}/admin/import/commit`, { upload_id: preview.upload_id, mappings: maps }, { headers });
      setCommitResult(r.data.results);
      setPreview(null);
      fetchBatches();
      toast.success('Import committed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Commit failed');
    }
    setCommitting(false);
  };

  const undo = async (b) => {
    if (!window.confirm(`Undo import "${b.sheet}" into ${b.kiosk_name}? This deletes ${b.inserted} imported orders.`)) return;
    setUndoing(b.id);
    try {
      const r = await axios.delete(`${API}/admin/import/batches/${b.id}`, { headers });
      toast.success(`Removed ${r.data.deleted_orders} imported orders`);
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    }
    setUndoing(null);
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  return (
    <div className="space-y-6" data-testid="import-history-page">
      <div>
        <h1 className="font-serif text-3xl text-charcoal-900">Import History</h1>
        <p className="text-sm text-charcoal-800/60 mt-1 max-w-2xl">
          Bring historical transactions into analytics. Upload → review the dry-run report → map each sheet to a booth → commit.
          Imports are duplicate-proof (keyed on Order ID) and fully reversible per batch.
        </p>
      </div>

      {/* Upload */}
      <label className="card-dashboard flex flex-col items-center justify-center py-10 border-2 border-dashed border-charcoal-900/15 cursor-pointer hover:bg-cream-100 transition-colors" data-testid="import-upload-zone">
        <UploadCloud className="w-10 h-10 text-charcoal-800/30 mb-3" />
        <span className="font-medium text-charcoal-900">{uploading ? 'Parsing & validating…' : 'Click to upload transactions file'}</span>
        <span className="text-xs text-charcoal-800/50 mt-1">.xlsx (transaction sheets auto-detected) or .csv · times read as IST · nothing is saved until you commit</span>
        <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} disabled={uploading} data-testid="import-file-input" />
      </label>

      {/* Dry-run report */}
      {preview && (
        <div className="space-y-4" data-testid="import-preview">
          <div className="flex items-center gap-2 text-sm text-charcoal-800/70">
            <FileSpreadsheet className="w-4 h-4" /> <span className="font-medium">{preview.filename}</span>
            {preview.cross_sheet_duplicates > 0 && (
              <span className="text-amber-700 bg-amber-100 rounded px-2 py-0.5 text-xs">
                {preview.cross_sheet_duplicates} order IDs appear in multiple sheets
              </span>
            )}
          </div>

          {preview.sheets.map((s) => (
            <div key={s.sheet} className="card-dashboard" data-testid={`import-sheet-${s.sheet}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h3 className="font-serif text-xl text-charcoal-900">{s.sheet}</h3>
                  <div className="flex items-center gap-3 text-xs text-charcoal-800/60 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> {s.date_min} → {s.date_max}</span>
                    <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> {inr(s.revenue_total)} revenue</span>
                    <span>{s.quantity_total} prints</span>
                    <span>{s.free_orders} free (voucher)</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={mappings[s.sheet] || ''}
                    onChange={(e) => setMappings({ ...mappings, [s.sheet]: e.target.value })}
                    className="px-3 py-2 border border-charcoal-900/15 rounded-lg text-sm bg-white"
                    data-testid={`import-booth-select-${s.sheet}`}
                  >
                    <option value="">— Don't import this sheet —</option>
                    {kiosks.map((k) => <option key={k.id} value={k.id}>Import into: {k.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Stat ok label="Valid rows" value={s.rows_valid} />
                <Stat warn={s.rows_rejected > 0} label="Rejected rows" value={s.rows_rejected} />
                <Stat warn={s.already_in_db > 0} label="Already in database" value={s.already_in_db} sub="will be skipped" />
                <Stat ok label="Will be inserted" value={Math.max(0, s.rows_valid - s.already_in_db)} />
              </div>

              {/* Month-by-month cross-check */}
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-xs">
                  <thead className="bg-cream-50">
                    <tr className="text-left">
                      <th className="px-3 py-2 uppercase text-charcoal-800/50">Month</th>
                      <th className="px-3 py-2 uppercase text-charcoal-800/50 text-right">Transactions</th>
                      <th className="px-3 py-2 uppercase text-charcoal-800/50 text-right">Prints</th>
                      <th className="px-3 py-2 uppercase text-charcoal-800/50 text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-900/5">
                    {s.monthly.map((mo) => (
                      <tr key={mo.month}>
                        <td className="px-3 py-1.5 font-mono">{mo.month}</td>
                        <td className="px-3 py-1.5 text-right">{mo.transactions}</td>
                        <td className="px-3 py-1.5 text-right">{mo.quantity}</td>
                        <td className="px-3 py-1.5 text-right font-medium">{inr(mo.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {s.rejected_samples.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs" data-testid={`import-rejected-${s.sheet}`}>
                  <div className="flex items-center gap-1.5 font-medium text-red-800 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Rejected rows (first {s.rejected_samples.length})
                  </div>
                  {s.rejected_samples.map((r, i) => (
                    <div key={i} className="text-red-700">Row {r.row} ({r.order_id}): {r.reason}</div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <button
            onClick={commit}
            disabled={committing || !Object.values(mappings).some(Boolean)}
            className="bg-charcoal-900 text-cream-50 px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-charcoal-800 disabled:opacity-40"
            data-testid="import-commit-btn"
          >
            {committing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            Commit import
          </button>
        </div>
      )}

      {/* Commit result */}
      {commitResult && (
        <div className="card-dashboard bg-green-50 border border-green-200" data-testid="import-commit-result">
          <div className="flex items-center gap-2 font-medium text-green-900 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" /> Import complete
          </div>
          {commitResult.map((r) => (
            <div key={r.id} className="text-sm text-green-900">
              <b>{r.sheet}</b> → {r.kiosk_name}: inserted <b>{r.inserted}</b>, skipped {r.skipped_existing} existing · {inr(r.revenue)} · {r.date_min} → {r.date_max}
            </div>
          ))}
          <p className="text-xs text-green-800/70 mt-2">Analytics now includes this data across all date filters.</p>
        </div>
      )}

      {/* Batches */}
      <div className="card-dashboard p-0 overflow-hidden">
        <h3 className="font-serif text-lg px-4 py-3 border-b border-charcoal-900/5">Past imports</h3>
        {batches.length === 0 ? (
          <div className="text-center py-8 text-sm text-charcoal-800/40">No imports yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="import-batches-table">
              <thead className="bg-cream-50">
                <tr className="text-left">
                  <th className="px-4 py-2.5 text-xs uppercase text-charcoal-800/50">When</th>
                  <th className="px-4 py-2.5 text-xs uppercase text-charcoal-800/50">Sheet → Booth</th>
                  <th className="px-4 py-2.5 text-xs uppercase text-charcoal-800/50 text-right">Inserted</th>
                  <th className="px-4 py-2.5 text-xs uppercase text-charcoal-800/50 text-right">Revenue</th>
                  <th className="px-4 py-2.5 text-xs uppercase text-charcoal-800/50">Range</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-900/5">
                {batches.map((b) => (
                  <tr key={b.id} className={b.deleted ? 'opacity-50' : ''} data-testid={`import-batch-${b.id}`}>
                    <td className="px-4 py-2.5 text-xs">{istDateTime(b.created_at)}<div className="text-charcoal-800/40">{b.created_by}</div></td>
                    <td className="px-4 py-2.5"><b>{b.sheet}</b> → {b.kiosk_name}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{b.inserted}</td>
                    <td className="px-4 py-2.5 text-right">{inr(b.revenue)}</td>
                    <td className="px-4 py-2.5 text-xs">{b.date_min} → {b.date_max}</td>
                    <td className="px-4 py-2.5 text-right">
                      {b.deleted ? (
                        <span className="text-xs text-red-600">Undone ({b.deleted_count} removed)</span>
                      ) : (
                        <button
                          onClick={() => undo(b)}
                          disabled={undoing === b.id}
                          className="text-xs px-2.5 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1 ml-auto disabled:opacity-50"
                          data-testid={`import-undo-${b.id}`}
                        >
                          {undoing === b.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="hidden" />
    </div>
  );
}

const Stat = ({ label, value, sub, ok, warn }) => (
  <div className={`rounded-lg border p-3 ${warn ? 'border-amber-300 bg-amber-50' : ok ? 'border-green-200 bg-green-50/50' : 'border-charcoal-900/10 bg-cream-50'}`}>
    <div className="text-xl font-semibold text-charcoal-900">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-charcoal-800/50">{label}{sub ? ` · ${sub}` : ''}</div>
  </div>
);
